import type { DisplayRole } from '@/features/auth/roles';
import { apiFetch } from '@/lib/api-client';

/** Public documentation variant (client tiers) — see ADR-013. */
export const PUBLIC_DOCS_PATH = '/docs/';
/** Internal documentation variant (all tiers) — gated by nginx auth_request. */
export const INTERNAL_DOCS_PATH = '/docs/internal/';

/**
 * Opens the documentation site in a new tab (SCRUM-731, ADR-013).
 *
 * Platform Admins first mint a docs session (POST /api/docs/session sets the
 * HttpOnly cookie scoped to /docs/internal) and land on the internal variant;
 * everyone else — and any admin whose mint fails (API down, key missing) —
 * falls back to the public docs. Fail-open to PUBLIC content only: the mint
 * failure path never grants anything, it just degrades the destination; the
 * actual gate is nginx auth_request server-side.
 *
 * Popup-blocker note: the tab is opened SYNCHRONOUSLY inside the caller's
 * click gesture and navigated after the async mint resolves — calling
 * window.open after an await gets blocked by default in Safari/Firefox.
 * `noopener` cannot be passed to window.open here because it makes the call
 * return null by spec; the opener link is severed manually instead.
 */
export async function openDocumentation(role: DisplayRole): Promise<void> {
  const win = window.open('', '_blank');
  if (win) {
    win.opener = null;
  }

  let target = PUBLIC_DOCS_PATH;
  if (role === 'Platform Admin') {
    try {
      const response = await apiFetch('/api/docs/session', { method: 'POST' });
      if (response.status === 204) {
        target = INTERNAL_DOCS_PATH;
      }
    } catch {
      // API unreachable — degrade to the public docs.
    }
  }

  if (win) {
    win.location.replace(target);
  } else {
    // Popup blocked: navigate the current tab as a last resort.
    window.location.assign(target);
  }
}
